import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import HonestyMeter from "@/components/HonestyMeter";
import EngagementBar from "@/components/EngagementBar";
import Comments from "@/components/Comments";
import ShareBar from "@/components/ShareBar";
import RazorpayButton from "@/components/RazorpayButton";
import { VerificationBadge, verificationInfo } from "@/components/VerificationBadge";
import { ExternalLink, ArrowLeft } from "lucide-react";
import { proxied } from "@/lib/images";
import { toast } from "sonner";

export default function QuoteDetail() {
  const { id } = useParams();
  const [q, setQ] = useState(null);
  const [buying, setBuying] = useState("");

  useEffect(() => {
    api.get(`/quotes/${id}`).then(r => setQ(r.data)).catch(() => setQ({ notfound: true }));
  }, [id]);

  const buy = async (lookup_key) => {
    try {
      setBuying(lookup_key);
      const origin_url = window.location.origin;
      const { data } = await api.post("/payments/checkout", { lookup_key, quantity: 1, origin_url });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Checkout unavailable. Try again shortly.");
      setBuying("");
    }
  };

  if (!q) return <div className="max-w-[900px] mx-auto p-10 font-quote text-2xl">Loading the record…</div>;
  if (q.notfound) return <div className="max-w-[900px] mx-auto p-10">
    <Link to="/feed" className="link-blue">← Back to the feed</Link>
    <h1 className="font-display text-4xl mt-4">Not found.</h1>
  </div>;

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-10 py-8 md:py-10 fade-up">
      <Link to="/feed" className="inline-flex items-center gap-2 font-ui text-xs hover:underline" data-testid="back-to-feed">
        <ArrowLeft size={14} /> Back to the feed
      </Link>

      <div className="grid grid-cols-12 gap-6 md:gap-8 mt-6">
        <article className="col-span-12 md:col-span-8">
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <span className="tag-red" data-testid="detail-category">{q.category}</span>
            <VerificationBadge q={q} size="lg" />
            <span className="font-mono text-xs opacity-70">{q.source_type?.toUpperCase()} · {q.year || "n.d."}</span>
          </div>

          {verificationInfo(q).key === "ai_illustrated" && (
            <div className="border-2 border-[#FF3333] bg-[#FFE9E9] p-4 mb-6" data-testid="ai-illustrated-notice">
              <div className="font-ui text-[10px] text-[#FF3333]">Editor's Disclaimer</div>
              <p className="font-quote text-base mt-1 italic">
                To be very honest — <b>nobody actually said this</b>. AI wrote it in the style of the archive to keep this page from feeling empty while we chase down real quotes. Enjoy the fiction responsibly.
              </p>
            </div>
          )}

          <blockquote className="font-quote text-3xl md:text-5xl lg:text-6xl leading-[1.05]" data-testid="detail-quote">
            “{q.text}”
          </blockquote>

          <div className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6 border-t-ink pt-6">
            <div className="flex items-center gap-4">
              {q.speaker_image_url && (
                <img
                  src={q.speaker_image_url}
                  alt={q.speaker}
                  referrerPolicy="no-referrer"
                  className="w-20 h-20 md:w-24 md:h-24 object-cover border border-[#0A0A0A] grayscale"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                  data-testid="detail-speaker-avatar"
                />
              )}
              <div>
                <div className="font-ui text-[10px] opacity-70">Attributed to</div>
                <div className="font-display text-2xl md:text-3xl" data-testid="detail-speaker">{q.speaker}</div>
                {q.speaker_role && <div className="font-mono text-xs opacity-70">{q.speaker_role}</div>}
                {q.source_url && (
                  <a href={q.source_url} target="_blank" rel="noreferrer"
                     className="link-blue mt-2 inline-flex items-center gap-1 text-sm"
                     data-testid="detail-source-link">
                    View source <ExternalLink size={12} />
                  </a>
                )}
              </div>
            </div>
            <div className="min-w-[180px]">
              <HonestyMeter score={q.honesty_score} />
              {q.honesty_rationale && (
                <p className="font-body text-xs opacity-80 mt-3 italic" data-testid="detail-rationale">
                  Editor's note: {q.honesty_rationale}
                </p>
              )}
            </div>
          </div>

          {/* Instagram-style engagement */}
          <div className="mt-6 border-t-ink pt-4 flex items-center justify-between">
            <EngagementBar quote={q} />
            <span className="font-mono text-[10px] opacity-60">Anonymous · one reaction per device</span>
          </div>

          <Comments quoteId={q.id} />
        </article>

        <aside className="col-span-12 md:col-span-4 space-y-6">
          <div className="border-ink bg-white p-6 shadow-brut-sm">
            <div className="font-ui text-xs text-[#FF3333]">Related — {q.category}</div>
            <h3 className="font-display text-2xl mt-2 leading-tight">What this quote triggers.</h3>
            <div className="mt-5 space-y-4" data-testid="related-affiliates">
              {(q.related_affiliates || []).slice(0, 4).map(a => (
                <a key={a.id} href={a.outbound_url} target="_blank" rel="noreferrer"
                   className="flex gap-3 group border-t border-[#0A0A0A]/20 pt-4"
                   data-testid={`affiliate-${a.id}`}>
                  <div className="img-frame w-20 h-20 flex-shrink-0"><img src={a.image_url} alt={a.title} loading="lazy" /></div>
                  <div className="flex-1">
                    <div className="font-ui text-[10px] opacity-70">{a.provider}</div>
                    <div className="font-display text-base leading-tight group-hover:underline">{a.title}</div>
                    <div className="font-mono text-xs mt-1">{a.price_label}</div>
                  </div>
                </a>
              ))}
              {(!q.related_affiliates || q.related_affiliates.length === 0) && (
                <div className="font-body text-sm opacity-70">No linked items yet.</div>
              )}
            </div>
          </div>

          <div className="border-ink bg-[#0A0A0A] text-[#F4F4F0] p-6 shadow-brut-sm">
            <div className="font-ui text-xs text-[#FF3333]">Wear it</div>
            <h3 className="font-display text-2xl mt-2 leading-tight">TBVH Merch</h3>
            <div className="mt-5 space-y-4" data-testid="related-merch">
              {(q.related_merch || []).slice(0, 3).map(m => (
                <div key={m.id} className="flex gap-3 border-t border-[#333] pt-4" data-testid={`merch-related-${m.id}`}>
                  <div className="img-frame w-20 h-20 flex-shrink-0"><img src={m.image_url} alt={m.title} loading="lazy" /></div>
                  <div className="flex-1">
                    <div className="font-display text-base leading-tight">{m.title}</div>
                    <div className="font-mono text-xs mt-1 opacity-80">${m.price_usd.toFixed(2)}</div>
                    <div className="mt-2 flex gap-2 flex-wrap">
                      <button
                        disabled={buying === m.lookup_key}
                        onClick={() => buy(m.lookup_key)}
                        className="font-ui text-[11px] text-[#FF3333] hover:underline"
                        data-testid={`buy-btn-${m.lookup_key}`}
                      >
                        {buying === m.lookup_key ? "Redirecting…" : "Stripe →"}
                      </button>
                    </div>
                    <RazorpayButton lookupKey={m.lookup_key} title={m.title} className="mt-2 !bg-transparent !text-[#F4F4F0] !border-[#F4F4F0]" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <ShareBar quote={q} />
        </aside>
      </div>
    </div>
  );
}
